import React from 'react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useAutoSave } from '@/hooks/useAutoSave';
import { AutoSaveIndicator } from '@/components/ui/AutoSaveIndicator';
import { type Sample } from '@/features/samples/services/sampleSearchService';
import { type Locality } from '@/features/alojamientos/hooks/useLocalities';
import { SampleUserType } from '@/features/create-samples/types/sample.types';
import { SamplingBlockReason } from '@/features/samples/services/samplingBlockReasonService';
import { SampleRecordStatus } from '@/features/samples/services/sampleRecordStatusService';
import { useNotificationCreate } from '@/features/notifications/hooks/useNotificationCreate';
import { updateSamplingBlockReason } from '@/features/alojamientos/api/accommodation.api';
import { getUserRole } from '@/features/auth/utils/auth';
import { ROLES, type Role } from '@/features/auth/constants/roles';

interface SampleRowProps {
  sample: Sample;
  localities: Locality[];
  sampleUserTypes?: SampleUserType[];
  samplingBlockReasons?: SamplingBlockReason[];
  sampleRecordStatuses: SampleRecordStatus[];
  showSpecialDate1: boolean;
  showSpecialDate2: boolean;
  showSpecialDate3: boolean;
  showSpecialDate4: boolean;
  specialDate1Title?: string;
  specialDate2Title?: string;
  specialDate3Title?: string;
  specialDate4Title?: string;
}

export const SampleRow: React.FC<SampleRowProps> = ({
  sample,
  localities,
  sampleUserTypes,
  samplingBlockReasons,
  sampleRecordStatuses,
  showSpecialDate1,
  showSpecialDate2,
  showSpecialDate3,
  showSpecialDate4,
  specialDate1Title,
  specialDate2Title,
  specialDate3Title,
  specialDate4Title
}) => {
  const { createNotification } = useNotificationCreate();
  const userRole = getUserRole() || 'fiscalizacion_observador';
  const canEditObservatoryNotes = [ROLES.ADMIN, ROLES.OBSERVATORIO_EDITOR, ROLES.OBSERVATORIO_OBSERVADOR].includes(userRole as Role);

  const {
    isSaving,
    lastSaved,
    hasUnsavedChanges,
    scheduleSave
  } = useAutoSave({
    sampleId: sample.id,
    sampleUpdatedAt: sample.updatedAt,
    delay: 1000,
    onDraftRestored: (restoredValues) => {
      const typed = restoredValues as Record<string, string | number | null>;
      pendingFieldsRef.current = { ...pendingFieldsRef.current, ...typed };
      setEditedValues(typed);
    },
    onSuccess: () => {},
    onError: (error) => {
      console.error('Error en auto-save:', error);
    },
  });

  const [editedValues, setEditedValues] = React.useState<Record<string, string | number | null>>({});

  // Synchronous accumulator for in-flight field changes. Unlike editedValues (React state),
  // this ref is always current at the time handleInputChange runs, preventing stale-closure
  // data loss when two fields change within the same render cycle.
  const pendingFieldsRef = React.useRef<Record<string, string | number | null>>({});

  // Seed the ref with current server values so every save payload is complete
  // even before any field has been edited by the user.
  React.useEffect(() => {
    pendingFieldsRef.current = {
      paxLastNight: sample.paxLastNight,
      occupiedRooms: sample.occupiedRooms,
      specialDate1: sample.specialDate1,
      specialDate2: sample.specialDate2,
      specialDate3: sample.specialDate3,
      specialDate4: sample.specialDate4,
      notes: sample.notes,
      sampleRecordStatusId: sample.sampleRecordStatusId,
      observatoryNotes: sample.accommodation.observatoryNotes ?? '',
    };
  }, [sample]);

  const handleInputChange = (field: string, value: string | number | null): void => {
    setEditedValues(prev => ({ ...prev, [field]: value }));

    if (field === 'samplingBlockReasonId') {
      void handleSamplingBlockReasonChange(value ?? 'null');
      return;
    }

    // Write the new value into the ref synchronously — this is always current
    // unlike editedValues state which may not have committed yet.
    pendingFieldsRef.current = { ...pendingFieldsRef.current, [field]: value };
    const ref = pendingFieldsRef.current;

    const updatedValues: Record<string, unknown> = {
      sampleDate: sample.sampleDate,
      accommodationId: sample.accommodationId,
      sampleTypeId: sample.sampleTypeId,
      sampleUserId: sample.sampleUserId,
      paxLastNight: ref.paxLastNight ?? sample.paxLastNight,
      occupiedRooms: ref.occupiedRooms ?? sample.occupiedRooms,
      specialDate1: ref.specialDate1 ?? sample.specialDate1,
      specialDate2: ref.specialDate2 ?? sample.specialDate2,
      specialDate3: ref.specialDate3 ?? sample.specialDate3,
      specialDate4: ref.specialDate4 ?? sample.specialDate4,
      notes: ref.notes ?? sample.notes,
      sampleRecordStatusId: ref.sampleRecordStatusId ?? sample.sampleRecordStatusId,
      createdByUserId: sample.createdByUserId,
      ...(canEditObservatoryNotes && {
        observatoryNotes: ref.observatoryNotes ?? sample.accommodation.observatoryNotes ?? '',
      }),
    };

    scheduleSave(updatedValues);
  };

  const handleSamplingBlockReasonChange = async (value: string | number | null) => {
    const newValue = value === 'null' ? null : value;
    const originalValue = sample.accommodation.samplingBlockReasonId ?? null;
    
    // Solo proceder si el valor cambió
    if (originalValue !== newValue) {
      // Actualizar inmediatamente el estado local para reflejar el cambio visual
      setEditedValues(prev => ({
        ...prev,
        samplingBlockReasonId: newValue
      }));

      try {
        // Crear notificación si se está bloqueando
        if (newValue !== null) {
          const description = editedValues.notes ?? sample.notes ?? '';
          await createNotification({
            accommodationId: String(sample.accommodationId),
            notificationStatusId: 1,
            notificationTypeId: 2,
            originUserRole: userRole,
            targetUserRole: 'fiscalizacion_editor',
            description: String(description),
          });
        }

        // Actualizar el accommodation usando el nuevo endpoint
        await updateSamplingBlockReason(
          String(sample.accommodationId),
          {
            samplingBlockReasonId: typeof newValue === 'number' ? newValue : null
          }
        );
      } catch (error) {
        console.error('Error al actualizar samplingBlockReasonId:', error);
        // Revertir el cambio en el estado local
        setEditedValues(prev => ({
          ...prev,
          samplingBlockReasonId: originalValue
        }));
      }
    }
  };


  const getInputValue = (field: string, defaultValue: string | number | null): string | number | null => {
    return editedValues[field] ?? defaultValue;
  };

  // Función para encontrar la localidad por ID
  const findLocality = (localityId: number) => {
    return localities.find(loc => loc.id === localityId);
  };

  const accommodationName = sample.accommodation.establishmentName || '-';
  const locality = sample.accommodation.localityId ? findLocality(Number(sample.accommodation.localityId))?.name || '-' : '-';
  const phone = sample.accommodation.phone || '';
  const whatsapp = sample.accommodation.whatsapp || '';

  const paxLastNight = typeof sample.paxLastNight === 'number' && sample.paxLastNight !== 0 ? sample.paxLastNight : '';
  const occupiedRooms = typeof sample.occupiedRooms === 'number' && sample.occupiedRooms !== 0 ? sample.occupiedRooms : '';
  const specialDate1 = sample.specialDate1 || '';
  const specialDate2 = sample.specialDate2 || '';
  const specialDate3 = sample.specialDate3 || '';
  const specialDate4 = sample.specialDate4 || '';
  const notes = sample.notes || '';
  const observatoryNotes = sample.accommodation.observatoryNotes ?? '';
  const sampleRecordStatusId = sample.sampleRecordStatusId || '-';
  // Calcular si está bloqueado considerando tanto el valor original como los cambios locales
  const currentSamplingBlockReasonId = editedValues.samplingBlockReasonId ?? sample.accommodation.samplingBlockReasonId;
  const isSamplingBlocked = currentSamplingBlockReasonId != null;

  return (
    <TableRow className={`${isSamplingBlocked ? 'bg-red-100' : ''}`}>
      <TableCell className={`sticky left-0 z-10 shadow-[2px_0_4px_-2px_rgba(0,0,0,0.15)] ${isSamplingBlocked ? 'bg-red-100' : 'bg-white'}`}>{accommodationName}</TableCell>
      <TableCell>{locality}</TableCell>
      <TableCell>{phone}</TableCell>
      <TableCell>{whatsapp}</TableCell>
      <TableCell>
        <Input
          type="number"
          value={getInputValue('paxLastNight', paxLastNight)}
          onChange={(e) => {
            const value = e.target.value === '' ? '' : Number(e.target.value);
            handleInputChange('paxLastNight', value);
          }}
          disabled={isSamplingBlocked}
          className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </TableCell>
      <TableCell>
        <Input
          type="number"
          value={getInputValue('occupiedRooms', occupiedRooms)}
          onChange={(e) => {
            const value = e.target.value === '' ? '' : Number(e.target.value);
            handleInputChange('occupiedRooms', value);
          }}
          disabled={isSamplingBlocked}
          className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
        />
      </TableCell>
      {showSpecialDate1 && (
        <TableCell>
          <Input
            type="text"
            value={getInputValue('specialDate1', specialDate1)}
            onChange={(e) => handleInputChange('specialDate1', e.target.value)}
            disabled={isSamplingBlocked}
            className="w-32"
          />
        </TableCell>
      )}
      {showSpecialDate2 && (
        <TableCell>
          <Input
            type="text"
            value={getInputValue('specialDate2', specialDate2)}
            onChange={(e) => handleInputChange('specialDate2', e.target.value)}
            disabled={isSamplingBlocked}
            className="w-32"
          />
        </TableCell>
      )}
      {showSpecialDate3 && (
        <TableCell>
          <Input
            type="text"
            value={getInputValue('specialDate3', specialDate3)}
            onChange={(e) => handleInputChange('specialDate3', e.target.value)}
            disabled={isSamplingBlocked}
            className="w-32"
          />
        </TableCell>
      )}
      {showSpecialDate4 && (
        <TableCell>
          <Input
            type="text"
            value={getInputValue('specialDate4', specialDate4)}
            onChange={(e) => handleInputChange('specialDate4', e.target.value)}
            disabled={isSamplingBlocked}
            className="w-32"
          />
        </TableCell>
      )}
      <TableCell style={{ display: 'none' }}>
        <Textarea
          value={getInputValue('notes', notes)}
          onChange={(e) => handleInputChange('notes', e.target.value)}
          disabled={isSamplingBlocked}
          className="w-40 min-h-[80px] resize-y"
          placeholder="Ingrese observaciones..."
        />
      </TableCell>
      <TableCell>
        <Textarea
          value={String(getInputValue('observatoryNotes', observatoryNotes) ?? '')}
          onChange={(e) => handleInputChange('observatoryNotes', e.target.value)}
          disabled={!canEditObservatoryNotes || isSamplingBlocked}
          maxLength={1000}
          className="w-40 min-h-[80px] resize-y"
          placeholder=""
        />
      </TableCell>
      <TableCell>
        <Select 
          value={String(getInputValue('sampleRecordStatusId', sample.sampleRecordStatusId))}
          onValueChange={(value) => handleInputChange('sampleRecordStatusId', Number(value))}
          disabled={isSamplingBlocked}
        >
          <SelectTrigger className="w-[140px]">
            <SelectValue placeholder="Estado de llamada">
              {sampleRecordStatuses.find(status => 
                status.id === getInputValue('sampleRecordStatusId', sample.sampleRecordStatusId)
              )?.name || 'Seleccionar estado'}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            {sampleRecordStatuses.map((status) => (
              <SelectItem key={status.id} value={String(status.id)}>
                {status.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <Select
          value={String(
            getInputValue('samplingBlockReasonId', 
              currentSamplingBlockReasonId ? String(currentSamplingBlockReasonId) : 'null'
            )
          )}
          disabled={Boolean(currentSamplingBlockReasonId) || isSamplingBlocked}
          onValueChange={(value) => handleInputChange('samplingBlockReasonId', value === 'null' ? null : Number(value))}
        >
          <SelectTrigger className="w-[160px] h-auto min-h-10 items-start pt-2 [&>span]:line-clamp-2 [&>span]:whitespace-normal [&>svg]:mt-0.5 [&>svg]:shrink-0">
            <SelectValue placeholder="Seleccionar">
              {(() => {
                const currentValue = getInputValue('samplingBlockReasonId',
                  currentSamplingBlockReasonId ? String(currentSamplingBlockReasonId) : 'null'
                );
                if (currentValue === 'null') return 'Seleccionar';
                const reason = samplingBlockReasons?.find(r => r.id === Number(currentValue));
                return reason?.name?.trim() || 'Seleccionar';
              })()}
            </SelectValue>
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="null">Seleccionar</SelectItem>
            {samplingBlockReasons?.map((reason) => (
              <SelectItem key={reason.id} value={String(reason.id)} className="whitespace-normal">
                {reason.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </TableCell>
      <TableCell>
        <AutoSaveIndicator
          isSaving={isSaving}
          lastSaved={lastSaved}
          hasUnsavedChanges={hasUnsavedChanges}
          className="text-xs"
        />
      </TableCell>
    </TableRow>
  );
};
